import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, ViewRef} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {combineLatest, forkJoin, Observable, of, Subscription} from 'rxjs';

import {Page} from '../../../domain/page';
import {Monitors} from '../../../infrastructure/http/monitor/monitors';
import {catchError, filter, take, takeUntil, tap} from "rxjs/operators";
import { MonitorStatusOverview } from 'app/domain/monitor/monitorStatusOverview';
import { MonitorDataService } from 'app/infrastructure/data/monitor-data.service';
import { AuthenticationService } from 'app/infrastructure/http/authentication/authentication.service';
import { MatDialog } from '@angular/material';
import { FirmwareUpdateDialogComponent } from './firmware-update-dialog/firmware-update-dialog.component';
import { FirmwareUpdateDialogV2Component } from './firmware-update-dialog-v2/firmware-update-dialog-v2.component';

@Component({
    selector: 'app-monitors',
    templateUrl: './monitors.component.html',
    styleUrls: ['./monitors.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MonitorsComponent {
    public monitors: MonitorStatusOverview[];
    public subPages: Page[] = [];
    public isLoading = true;

    private monitorsService: Monitors;
    private readonly FIRMWARE_DIALOG_KEY = 'firmware_update_2066_dismissed';
    private readonly FIRMWARE_DIALOG_V2_KEY = 'firmware_update_2066_post_deadline_dismissed';
    private readonly FIRMWARE_DEADLINE = new Date('2026-06-20');
    private readonly FIRMWARE_DIALOG_END = new Date('2026-11-01');
    private firmwareDialogChecked = false;
    subscription: Subscription;

    constructor(monitorsService: MonitorDataService,
                route: ActivatedRoute,
                private cdr: ChangeDetectorRef,
                private auth: AuthenticationService,
                private dialog: MatDialog) {
        this.monitorsService = monitorsService;
        this.subPages = route.snapshot.data.subPages;
    }

    ngOnInit() {
        const tapFunction = tap(() => setTimeout(() => {this.isLoading = false; this.detectChanges()}, 1));
        this.subscription = this.monitorsService.getAllForStatusOverview()
            .pipe(tapFunction)
            .subscribe((monitors: MonitorStatusOverview[]) => {
                this.monitors = monitors;
                this.detectChanges();
                if (!this.firmwareDialogChecked) {
                    this.firmwareDialogChecked = true;
                    this.checkFirmwareUpdateDialog(monitors);
                }
            });
    }

    private checkFirmwareUpdateDialog(monitors: MonitorStatusOverview[]): void {
        const now = new Date();

        if (now >= this.FIRMWARE_DIALOG_END || monitors.length === 0) {
            return;
        }

        const isPostDeadline = now >= this.FIRMWARE_DEADLINE;
        const dialogKey = isPostDeadline ? this.FIRMWARE_DIALOG_V2_KEY : this.FIRMWARE_DIALOG_KEY;

        if (window.localStorage.getItem(dialogKey) === 'true') {
            return;
        }

        const firmwareChecks = monitors.map(m =>
            this.monitorsService.getFirmwareUpgradeStatus(m.id).pipe(take(1), catchError(() => of(null)))
        );

        forkJoin(firmwareChecks).subscribe(statuses => {
            const hasOutdated = statuses
                .filter(s => s !== null)
                .some(s => s.currentVersion < 2066);

            if (hasOutdated) {
                const component = isPostDeadline ? FirmwareUpdateDialogV2Component : FirmwareUpdateDialogComponent;
                const dialogRef = this.dialog.open(component as any, {
                    maxWidth: '680px',
                    width: '95vw'
                });
                dialogRef.afterClosed().subscribe((dontShowAgain: boolean) => {
                    if (dontShowAgain) {
                        window.localStorage.setItem(dialogKey, 'true');
                    }
                });
            }
        });
    }

    ngOnDestroy() {
        this.subscription.unsubscribe()
    }

    public fetchDetails(monitor) {
        const details = {...monitor.details, info: monitor.monitorType, serialNumber: monitor.serialNumber};
        return details
    }

    private detectChanges() {
        if(this.cdr && !(this.cdr as ViewRef).destroyed) {
            this.cdr.detectChanges()
        }
    }
}
